import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PRIMARY_TRADING_ACCOUNT_KEY } from '../assets/constants'
import { RefreshTimeDuration } from '../components/screener/StockScreener'
import { User } from '../proto/generated/identity'
import { PremiumFeature, SubscriptionFeatures } from '../proto/generated/subscription'
import { SuperChartConfiguration_TimeBucket } from '../proto/generated/superchart-page'
import { Broker, TradingAccount, TradingAccountMargin } from '../proto/generated/trading-bridge'
import { Platform, UserDashboardProto } from '../proto/generated/user-preference'
import { TradeOptions } from './scalper.reducer'
import { ScalperLayouts } from '../pages/scalper/scalper.typings'
import { PredefinedDashboard } from '../components/widgetDashboard/widgetDashboard.enums'


export type ChartPreferences = {
    autoSave: boolean
}

export type ScreenerPreferences = {
    autoSave: boolean,
    refreshDuration: RefreshTimeDuration,
    displayCompanyName: boolean,
    displayCurrencySymbol: boolean,
    displayExchange: boolean,
}

export enum QuickWidgetTypes {
    MARKET_DEPTH = "MARKET_DEPTH",
    ORDER = "ORDER",
    POSITION = "POSITION",
    QUICK_TRADE = "QUICK_TRADE"
}

export type QuickWidgetConfig<T = {}> = {
    title: string,
    enabled: boolean,
    visible: boolean,
    position?: {
        left: number,
        top: number,
    },
    dimensions: {
        width: number,
        height: number
    },
    order: number,
    settings?: T
    QuickmodeEnabled?: boolean
}

export type QuickWidgetsState = Partial<Record<QuickWidgetTypes, QuickWidgetConfig>>

export type WatchListPreferences = {
    tableView: boolean,
    customizedColumn: {
        last: boolean,
        change: boolean,
        changePercentage: boolean,
        volume: boolean,
        showLogo: boolean
    }
}
export type CustomizedColumnInWatchList = {
    last: boolean,
    change: boolean,
    changePercentage: boolean,
    volume: boolean,
    showLogo: boolean
}

export type ScalperPreferences = {
    tradeOptions: TradeOptions,
    scalperLayout: ScalperLayouts,
    predefinedLayout: PredefinedDashboard
}

export type PayOffSettings = {
    showSdLines: boolean,
    showOiBars: boolean
}

export type UserPreferences = {
    chart: ChartPreferences,
    favoriteTimeBuckets: string[],
    favoriteChartTypes: string[],
    customTimeBuckets: SuperChartConfiguration_TimeBucket[],
    screener: ScreenerPreferences,
    payOffSettings: PayOffSettings
    watchlist: WatchListPreferences,
    quickWidgets: QuickWidgetsState,
    scalperSettings?: ScalperPreferences
}
export interface UserDetailType {
    authenticated: boolean
    currentUser: User | null,
    preferences: UserPreferences

}

export enum Group {
    SIMULATOR = 'SIMULATOR',
    CHART = 'CHART',
    TRADING = 'TRADING',
    MULTI_STRIKE = 'MULTI_STRIKE',
    SCALPER = 'SCALPER',
    STRATEGY_BUILDER = 'STRATEGY_BUILDER',
    SCREENER = 'SCREENER',
    OPTION_CHAIN = 'OPTION_CHAIN',
}

export type UserStateType = {
    userDetails: UserDetailType,
    subscriptionPlans: Record<string, PremiumFeature[]> | undefined
    allUserFeatures: PremiumFeature[] | undefined
    featureRestrictionDialog: {
        open: boolean
        feature: string
        featureRestrictionCode?: string
        group?: Group,
        revertToHome?: boolean
    }
    tradingAccounts: TradingAccount[] | undefined
    primaryTradingAccount: TradingAccount | undefined
    brokers: Broker[]
    margins: Record<string, TradingAccountMargin>
    userDashboards: UserDashboardProto[],
    userDashboardsLoaded: boolean,
    predefinedDashboards: UserDashboardProto[]
}

export const initialState: UserStateType = {
    userDetails: {
        authenticated: false,
        currentUser: null,
        preferences: {
            chart: {
                autoSave: true
            },
            favoriteTimeBuckets: [],
            favoriteChartTypes: [],
            customTimeBuckets: [],
            screener: {
                autoSave: false,
                refreshDuration: '',
                displayCompanyName: false,
                displayCurrencySymbol: false,
                displayExchange: false,
            },
            payOffSettings: {
                showSdLines: true,
                showOiBars: true
            },
            watchlist: {
                tableView: false,
                customizedColumn: {
                    last: true,
                    change: true,
                    changePercentage: true,
                    volume: false,
                    showLogo: true
                }
            },
            quickWidgets: {},
        }
    },
    subscriptionPlans: undefined,
    allUserFeatures: undefined,
    featureRestrictionDialog: {
        open: false,
        feature: ''
    },
    tradingAccounts: undefined,
    primaryTradingAccount: undefined,
    brokers: [],
    margins: {},
    userDashboards: [],
    userDashboardsLoaded: false,
    predefinedDashboards: []
}

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserDetails(state: UserStateType, action: PayloadAction<Partial<UserDetailType>>) {
            const { currentUser } = action.payload;
            const parsedPreferences = currentUser?.preferences
                ? JSON.parse(currentUser.preferences)
                : {};
            return {
                ...state,
                userDetails: {
                    ...state.userDetails,
                    ...action.payload,
                    preferences: {
                        ...state.userDetails.preferences,
                        ...parsedPreferences
                    }
                }
            };
        },
        setAllFeatures(state: UserStateType, action: PayloadAction<PremiumFeature[]>) {
            return {
                ...state,
                allUserFeatures: action.payload
            }
        },
        setUserSubscriptionPlansData(state: UserStateType, action: PayloadAction<SubscriptionFeatures[]>) {
            const data = {} as Record<string, PremiumFeature[]>
            action.payload.forEach(sub => {
                data[sub.subscriptionPlan] = sub.premiumFeature
            })
            return {
                ...state,
                subscriptionPlans: data
            }
        },
        setFeatureRestrictionDialog(state: UserStateType, action: PayloadAction<{
            open: boolean
            feature: string,
            group?: Group,
            revertToHome?: boolean 
            featureRestrictionCode?: string
        }>) {
            return {
                ...state,
                featureRestrictionDialog: action.payload
            }
        },
        setBrokers(state: UserStateType, action: PayloadAction<Broker[]>) {
            return {
                ...state,
                brokers: [...action.payload]
            }
        },
        setTradingAccounts(state: UserStateType, action: PayloadAction<TradingAccount[]>) {
            const sessionTradingAccountId = sessionStorage.getItem(PRIMARY_TRADING_ACCOUNT_KEY)
            const primaryTradingAccount = action.payload.length > 0 ? (action.payload as TradingAccount[]).find(t => t.primary) : undefined
            if (!sessionTradingAccountId) {
                if (primaryTradingAccount) {
                    sessionStorage.setItem(PRIMARY_TRADING_ACCOUNT_KEY, primaryTradingAccount.accountId)
                }
            } else {
                if (!action.payload?.find(t => t.accountId === sessionTradingAccountId)) {
                    if (primaryTradingAccount) {
                        sessionStorage.setItem(PRIMARY_TRADING_ACCOUNT_KEY, primaryTradingAccount.accountId)
                    }
                }
            }
            return {
                ...state,
                tradingAccounts: [...action.payload],
                primaryTradingAccount: primaryTradingAccount
            }
        },
        setMarginForAccount(state: UserStateType, action: PayloadAction<{ accountId: string; margin: TradingAccountMargin }>) {
            const { accountId, margin } = action.payload
            return {
                ...state,
                margins: {
                    ...state.margins,
                    [accountId]: margin
                }
            }
        },
        setUserDashboards(state: UserStateType, action: PayloadAction<UserDashboardProto[]>) {
            const { payload } = action;
            if (!payload.length) {
                return state;
            }
            const userDashboards: UserDashboardProto[] = [];
            const predefinedDashboards: UserDashboardProto[] = [];
            (payload as UserDashboardProto[]).forEach((dashboard) => {
                if (dashboard.isPredefined) {
                    predefinedDashboards.push(dashboard)
                } else {
                    userDashboards.push(dashboard)
                }
            });
            return {
                ...state,
                userDashboards,
                predefinedDashboards,
                userDashboardsLoaded: true
            }
        },
        setDefaultDashboard(state: UserStateType, action: PayloadAction<{ id: string; platform: Platform }>) {
            const { id, platform } = action.payload
            state.userDashboards = state.userDashboards.map((dashboard) => {
                if (dashboard.platform !== platform) return dashboard
                return {
                    ...dashboard,
                    isDefault: dashboard.id === id
                }
            })
        },
        updateUserDashboard(state: UserStateType, action: PayloadAction<UserDashboardProto>) {
            const payload = action.payload;
            const newUserDashboards: UserDashboardProto[] = [];
            let layoutPresent = false;
            state.userDashboards.forEach((d) => {
                if (d.id === payload.id) {
                    layoutPresent = true;
                    newUserDashboards.push(payload);
                } else {
                    newUserDashboards.push(d);
                }
            })

            if (!layoutPresent) {
                newUserDashboards.unshift(payload)
            }
            return {
                ...state,
                userDashboards: newUserDashboards
            }
        },
        deleteUserDashboard(state: UserStateType, action: PayloadAction<{ deletedLayoutId: string, defaultLayoutId: string }>) {
            const { deletedLayoutId, defaultLayoutId } = action.payload;
            const newDashboards: UserDashboardProto[] = [];
            state.userDashboards.forEach((d) => {
                if (d.id === deletedLayoutId) return;

                if (d.id === defaultLayoutId) {
                    newDashboards.push({
                        ...d,
                        isDefault: true
                    })
                } else {
                    newDashboards.push(d);
                }
            })
            return {
                ...state,
                userDashboards: newDashboards
            }
        },
        updateUserPreferencesInState(state: UserStateType, action: PayloadAction<UserPreferences>) {
            return {
                ...state,
                userDetails: {
                    ...state.userDetails,
                    preferences: {
                        ...state.userDetails.preferences,
                        ...action.payload
                    },

                },
            };
        },
        updateQuickWidgetConfig(state: UserStateType, action: PayloadAction<{ widgetType: QuickWidgetTypes, config: Partial<QuickWidgetConfig> }>) {
            const { widgetType, config } = action.payload;
            const existingWidgets = state.userDetails.preferences.quickWidgets || {}
            return {
                ...state,
                userDetails: {
                    ...state.userDetails,
                    preferences: {
                        ...state.userDetails.preferences,
                        quickWidgets: {
                            ...existingWidgets,
                            [widgetType]: {
                                ...(existingWidgets[widgetType] || {}),
                                ...config
                            }
                        }
                    },
                },
            }
        },
    }
})

const { actions, reducer } = userSlice

export const { setUserDetails, setTradingAccounts, setAllFeatures, setUserSubscriptionPlansData, setFeatureRestrictionDialog, setBrokers, setMarginForAccount, setUserDashboards, setDefaultDashboard, updateUserDashboard, deleteUserDashboard, updateUserPreferencesInState, updateQuickWidgetConfig } = actions

export default reducer